const LOCALE_PATH_PATTERN = /^\/(zh|en)(\/|$)/
const inFlightApiGetRequests = new Map<string, Promise<Response>>()

function resolveLocaleFromPath(pathname: string): string {
  const match = pathname.match(LOCALE_PATH_PATTERN)
  return match?.[1] ?? 'zh'
}

export function getPageLocale(): string {
  if (typeof window === 'undefined') return 'zh'
  return resolveLocaleFromPath(window.location.pathname)
}

function resolveRequestPathname(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    if (input.startsWith('/')) {
      const queryIndex = input.indexOf('?')
      return queryIndex >= 0 ? input.slice(0, queryIndex) : input
    }
    try {
      return new URL(input).pathname
    } catch {
      return ''
    }
  }

  if (input instanceof URL) {
    return input.pathname
  }

  try {
    return new URL(input.url).pathname
  } catch {
    return ''
  }
}

function resolveRequestPathWithSearch(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    if (input.startsWith('/')) return input
    try {
      const url = new URL(input)
      return `${url.pathname}${url.search}`
    } catch {
      return input
    }
  }

  if (input instanceof URL) {
    return `${input.pathname}${input.search}`
  }

  try {
    const url = new URL(input.url)
    return `${url.pathname}${url.search}`
  } catch {
    return ''
  }
}

function shouldInjectLocaleHeader(input: RequestInfo | URL): boolean {
  const pathname = resolveRequestPathname(input)
  return pathname === '/api' || pathname.startsWith('/api/')
}

export function mergeLocaleHeader(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers)
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', getPageLocale())
  }
  return { ...init, headers }
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const mergedInit = shouldInjectLocaleHeader(input) ? mergeLocaleHeader(init) : init
  const method = (mergedInit?.method || 'GET').toUpperCase()
  const requestPathname = resolveRequestPathname(input)
  const requestPathWithSearch = resolveRequestPathWithSearch(input)
  const canDedupe =
    method === 'GET' &&
    requestPathname.startsWith('/api/') &&
    !mergedInit?.signal

  if (!canDedupe) {
    return fetch(input, mergedInit)
  }

  const locale =
    mergedInit?.headers instanceof Headers
      ? mergedInit.headers.get('Accept-Language') || ''
      : new Headers(mergedInit?.headers).get('Accept-Language') || ''
  const requestKey = `${method}:${requestPathWithSearch}:${locale}`
  const existingRequest = inFlightApiGetRequests.get(requestKey)
  if (existingRequest) {
    const sharedResponse = await existingRequest
    return sharedResponse.clone()
  }

  const requestPromise = fetch(input, mergedInit).finally(() => {
    inFlightApiGetRequests.delete(requestKey)
  })
  inFlightApiGetRequests.set(requestKey, requestPromise)
  const response = await requestPromise
  return response.clone()
}
